import type { LandingDestination } from '../../hooks/useLandingDestinations';
import { sameDestination } from '../../tracking-link';
import type { TrackingLink } from '../../tracking-links-api';

/**
 * The landing page a tracking link is sent to (undefined everywhere means the casino, the default). A Studio page is
 * known by its spark id (`landingPageId`), which outlives a republish at a new address; a live page Studio did not
 * make is known by its URL alone. `url` is '' for a Studio page never published (a quick landing page still being
 * built), `name` labels such a page while the brand's list does not show it.
 */
export interface LandingChoice {
  url: string;
  landingPageId?: string;
  name?: string;
}

/** A landing page choice's key (a list row's React key): its Studio id when it has one, else its URL. Pure. */
export const choiceKey = (choice: Pick<LandingChoice, 'url' | 'landingPageId'>): string =>
  choice.landingPageId ? `lp:${choice.landingPageId}` : `url:${choice.url.trim()}`;

/** Whether two choices are the same page: by Studio id when both have one, else by address. Pure. */
export const sameChoice = (
  a: Pick<LandingChoice, 'url' | 'landingPageId'>,
  b: Pick<LandingChoice, 'url' | 'landingPageId'>,
): boolean =>
  a.landingPageId && b.landingPageId ? a.landingPageId === b.landingPageId : sameDestination(a.url, b.url);

/** A listed destination as a choice. Pure. */
export const choiceOf = (d: LandingDestination): LandingChoice =>
  d.landingPageId ? { url: d.url, landingPageId: d.landingPageId } : { url: d.url };

/**
 * Where a shown link lands, as the sheet's choice: the casino (undefined); the listed page with the link's Studio
 * id; a Studio page the list does not show (not live: unpublished, failed, or never published yet), kept by its id and
 * labelled by its name; else the listed page at the link's address, or that address alone. Pure.
 */
export const choiceForLink = (
  link: Pick<TrackingLink, 'destination_type' | 'landing_page_id' | 'destination_url' | 'landing_page'>,
  destinations: readonly LandingDestination[],
): LandingChoice | undefined => {
  if (link.destination_type !== 'landing_page') return undefined;
  if (link.landing_page_id) {
    const listed = destinations.find((d) => d.landingPageId === link.landing_page_id);
    if (listed) return choiceOf(listed);
    return {
      url: link.landing_page?.url || link.destination_url,
      landingPageId: link.landing_page_id,
      name: link.landing_page?.name ?? '',
    };
  }
  const listed = destinations.find((d) => sameDestination(d.url, link.destination_url));
  return listed ? choiceOf(listed) : { url: link.destination_url };
};
